import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {LightV1, fromRenderStructDTOArray, RenderLayout, LightV3, LightV2} from '@3dcalc/3dpClient';
import * as THREE from 'three';
import {SceneEnvironment} from "@3dcalc/3dpClient/dist/components/canvas/SceneEnvironment";

const BASE_URL = '/model/';

type ModelDef = {
    id: string;
    label: string;
    camera: [number, number, number];
    position: [number, number, number];
    url: string;
};

const MODELS: ModelDef[] = [
    { id: '787d8945-c592-4c1d-9727-7d6a8c8df603', label: '', camera: [15, 0, 2] as [number,number,number], position: [0, -2, 0], url: BASE_URL+'787d8945-c592-4c1d-9727-7d6a8c8df603.json' },
    { id: '551ec3f2-e986-4332-a06e-f975b298ac6a', label: '', camera: [12, -4, 0] as [number,number,number], position: [0, -2, 0], url: BASE_URL+'551ec3f2-e986-4332-a06e-f975b298ac6a.json' },
    { id: 'd5dfc0ef-96a2-4cb6-9d39-c37ffdbd9e8d', label: '', camera: [15, 0, 2] as [number,number,number], position: [0, -2, 0], url: BASE_URL+'d5dfc0ef-96a2-4cb6-9d39-c37ffdbd9e8d.json' },
    { id: '0cace72b-30bd-4c0e-97c9-ac6e402ec61a', label: '', camera: [5, -3, 10] as [number,number,number], position: [0, -1, 0], url: BASE_URL+'0cace72b-30bd-4c0e-97c9-ac6e402ec61a.json' },
];

// ─── Camera ─────────────────────────────────────────────────────────────────
const CameraRig = ({ position }: { position: [number, number, number] }) => {
    const { camera } = useThree();
    const target = useRef(new THREE.Vector3(...position));

    useEffect(() => {
        target.current.set(...position);
    }, [position]);

    useFrame((_, delta) => {
        const k = 1 - Math.exp(-1.5 * delta);
        camera.position.lerp(target.current, k);
        camera.lookAt(0, 0, 0);
    });

    return null;
};

// ─── Scene ──────────────────────────────────────────────────────────────────
const AutoRotateScene = ({ model, struct }: { model: any; struct: any[] }) => {
    const group = useRef<THREE.Group>(null);
    const [palette] = useState([]);

    useFrame((_, delta) => {
        if (group.current) group.current.rotation.y += delta * 0.4;
    });

    return (
        <group ref={group} position={model.position}>
            <RenderLayout struct={struct} palette={palette}
                          onPaletteChange={() => {}} onColorChange={() => {}} />
        </group>
    );
};

// ─── Slider ─────────────────────────────────────────────────────────────────
const ConfiguratorPreview = () => {
    const [index, setIndex] = useState(0);
    const [shown, setShown] = useState({ model: MODELS[0], struct: [] as any[] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const model = MODELS[index];
        let cancelled = false;
        setLoading(true);

        fetch(model.url)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(data => {
                if (cancelled) return;
                setShown({ model, struct: fromRenderStructDTOArray(data) });
            })
            .catch(err => {
                if (!cancelled) console.error('Model load failed:', model.url, err);
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [index]);

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex(i => (i + 1) % MODELS.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [index]);

    const prev = () => setIndex(i => (i - 1 + MODELS.length) % MODELS.length);
    const next = () => setIndex(i => (i + 1) % MODELS.length);

    return (
        <div className="relative w-full h-full overflow-hidden" style={{ background: '#ffffff' }}>
            <Suspense fallback={
                <div className="w-full h-full flex items-center justify-center">
                    <span className="text-white/20 text-sm tracking-widest uppercase">Loading…</span>
                </div>
            }>
                <Canvas camera={{ position: MODELS[0].camera as [number,number,number], fov: 30 }}
                        gl={{ antialias: true }}>
                    <LightV1 />
                    <CameraRig position={shown.model.camera} />
                    <AutoRotateScene model={shown.model} struct={shown.struct} />
                    <SceneEnvironment sky={false} floor={false}></SceneEnvironment>
                </Canvas>
            </Suspense>

            <div className={`absolute inset-0 pointer-events-none transition-opacity duration-200
                             ${loading ? 'opacity-60' : 'opacity-0'}`} />

            <button onClick={prev} aria-label="Previous model"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full
                               bg-black/40 hover:bg-black/60 border border-white/10
                               flex items-center justify-center text-white/70 hover:text-white
                               transition-colors backdrop-blur-sm">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5"
                          strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <button onClick={next} aria-label="Next model"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full
                               bg-black/40 hover:bg-black/60 border border-white/10
                               flex items-center justify-center text-white/70 hover:text-white
                               transition-colors backdrop-blur-sm">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5"
                          strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            <div className="absolute bottom-3 inset-x-0 flex flex-col items-center gap-2 pointer-events-none">
                <span className="text-white/50 text-xs tracking-widest uppercase">
                    {MODELS[index].label}
                </span>
                <div className="flex items-center gap-1.5 pointer-events-auto">
                    {MODELS.map((m, i) => (
                        <button key={m.id} onClick={() => setIndex(i)}
                                aria-label={m.label}
                                className={`h-1.5 rounded-full transition-all duration-200 ${
                                    i === index ? 'w-5 bg-emerald-500' : 'w-1.5 bg-white/25 hover:bg-white/40'
                                }`} />
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── App ────────────────────────────────────────────────────────────────────
export default function App() {
    return <ConfiguratorPreview />;
}
